import React, { FC, ReactElement } from 'react';
// Components
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Space, { Type } from '../layout/space';
import SvgIcon from '../icons/SvgIcon';
import InfoBoxTex from '../infoBoxText';
import Text from '../text';
import TextCenterButton from '../buttons/textCenterButton';
// Resources
import { IconCommon, IconLogo } from '../../resources/images';
import { stringsRes } from '../../resources/strings';
import { isUserFullB2C } from '../../utils/user';
import { CreatePromotionInput } from '../../core/apiTypes';
import { useCreatePromotion } from '../../core/services/promotions';

interface PropsFromModal {
    onRequestClose?: () => void;
}

interface MarcelProps extends PropsFromModal {
    title?: string;
    description?: string;
    labelCancel?: string;
    hasCancel?: boolean;
    labelConfirm?: string;
    onConfirmAction?: () => void;
    onCancelAction?: () => void;
}

const Marcel: FC<MarcelProps> = (props): ReactElement => {
    const {
        onRequestClose,
        title = stringsRes.external.popup.title,
        description = stringsRes.external.popup.details,
        labelCancel,
        hasCancel = false,
        labelConfirm,
        onConfirmAction,
        onCancelAction,
    } = props;
    const classes = useStyle();

    const createPromotion = useCreatePromotion();

    const userMode = isUserFullB2C() ? 'b2c' : 'b2b';
    const isB2C = userMode === 'b2c';

    const onClickConfirm = (): void => {
        if (isB2C) {
            const payload: CreatePromotionInput = {
                promo_code: 'WELCOME8',
            };
            createPromotion.mutate(payload, {
                onSuccess: () => {
                    if (onConfirmAction) {
                        onConfirmAction();
                    }
                    onRequestClose();
                },
                onError: () => {
                    if (onConfirmAction) {
                        onConfirmAction();
                    }
                    onRequestClose();
                },
            });
        } else {
            if (onConfirmAction) {
                onConfirmAction();
            }
            onRequestClose();
        }
    };

    const onClickCancel = (): void => {
        if (onCancelAction) {
            onCancelAction();
        }
        onRequestClose();
    };

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <div className={classes.titleContainer}>
                    <Text.H1 text={title} className={classes.title} />
                    <span onClick={onClickCancel} className={classes.closeIcon}>
                        <SvgIcon icon={IconCommon.Close} color="black" size="large" />
                    </span>
                </div>
                <Space value={2} />
                <div className={classes.logoContainer}>
                    <SvgIcon icon={IconLogo.LogoMarcel} size="inherit" stroke="none" />
                    <ArrowForwardIcon />
                    <SvgIcon icon={IconLogo.Logo} size="inherit" stroke="none" />
                </div>
                {isB2C && (
                    <>
                        <Space type={Type.vertical} value={2} />
                        <Text.Description14 text={description} />
                    </>
                )}
            </div>

            <Space type={Type.vertical} value={2} />
            <InfoBoxTex
                title={stringsRes.external.popup.info[userMode].title}
                titleIcon={isB2C ? null : 'info'}
                description={stringsRes.external.popup.info[userMode].details}
            />

            <div className={classes.buttonContainer}>
                <TextCenterButton
                    id="download"
                    title={labelConfirm ?? stringsRes.external.popup.download}
                    onClick={onClickConfirm}
                />
                {hasCancel && (
                    <TextCenterButton
                        id="cancel"
                        title={labelCancel ?? stringsRes.common.cancel}
                        className={classes.button}
                        onClick={onClickCancel}
                    />
                )}
            </div>
        </div>
    );
};

const useStyle = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            // marginRight: theme.spacing(3),
            // marginLeft: theme.spacing(3),
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            // alignItems: 'center',
            justifyContent: 'center',
        },
        titleContainer: {
            display: 'flex',
            placeItems: 'center',
            justifyContent: 'space-between',
        },
        title: {
            fontSize: 26,
        },
        closeIcon: {
            alignSelf: 'baseline',
            cursor: 'pointer',
        },
        logoContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.colors.background.primaryElement,
            borderRadius: 10,
            columnGap: 16,
            padding: '20px 0px',
        },
        h4: {
            fontWeight: 900,
            fontFamily: 'Madras',
        },
        buttonContainer: {
            display: 'flex',
            marginTop: theme.spacing(3),
            flexDirection: 'column',
            justifyContent: 'center',
        },
        button: {},
    }),
);

export default Marcel;
